import { memo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PropTypes from "prop-types";

import TooltipLabel from "components/TooltipLabel";
import theme from "themeProvider";

const tooltipStyle = {
  padding: 0,
  borderRadius: `${theme.shape.borderRadius}px`,
  overflow: "hidden",
};

const cellBorder = `1px solid ${theme.palette.grey[300]}`;

// Static selector-based styles: no per-cell style objects on render
const tableStyle = {
  "& th, & td": {
    fontSize: 12,
    whiteSpace: "nowrap",
    padding: "6px 12px",
    borderBottom: cellBorder,
  },
  "& th": {
    fontWeight: 700,
    // Theme rounds the first/last header cells; keep header flush inside the tooltip
    borderRadius: "0 !important",
  },
  "& td:not(:last-child)": { borderRight: cellBorder },
  "& td:first-of-type": { fontWeight: 600 },
};

const titleStyle = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  py: 1,
};

const emptyCellStyle = {
  color: theme.palette.text.secondary,
  fontWeight: "400 !important",
};

const getCellValue = (row, { key, format }) => {
  const value = row?.[key];
  if (format) return format(value, row);
  return value ?? "-";
};

const TableContent = memo(({ title, titleIcon, columns, rows, emptyText }) => (
  <Box>
    {title && (
      <Box sx={titleStyle}>
        {titleIcon}
        <Typography
          fontSize={14}
          fontWeight={600}
          color={theme.palette.text.primary}
        >
          {title}
        </Typography>
      </Box>
    )}
    <TableContainer>
      <Table size="small" sx={tableStyle}>
        <TableHead>
          <TableRow>
            {columns.map(({ key, label }) => (
              <TableCell key={key}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.length ? (
            rows.map((row, rowIndex) => (
              <TableRow key={rowIndex} className="row-no-bg">
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {getCellValue(row, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow className="row-no-bg">
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={emptyCellStyle}
              >
                {emptyText}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
));

/**
 * Info icon that shows a table inside a light tooltip on hover.
 * columns: [{ key, label, format?: (value, row) => node }]
 */
const InfoTableTooltip = ({
  title,
  titleIcon,
  columns,
  rows,
  emptyText = "No data available",
  placement = "bottom-start",
  iconSize = 18,
}) => (
  <TooltipLabel
    placement={placement}
    customStyle={tooltipStyle}
    title={
      <TableContent
        title={title}
        titleIcon={titleIcon}
        columns={columns}
        rows={rows}
        emptyText={emptyText}
      />
    }
    labelChildren={
      <InfoOutlinedIcon
        data-testid="info-table-tooltip-icon"
        sx={{
          fontSize: iconSize,
          color: theme.palette.primary.main,
          cursor: "pointer",
        }}
      />
    }
  />
);

InfoTableTooltip.propTypes = {
  title: PropTypes.string,
  titleIcon: PropTypes.element,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      format: PropTypes.func,
    }),
  ).isRequired,
  rows: PropTypes.array,
  emptyText: PropTypes.string,
  placement: PropTypes.string,
  iconSize: PropTypes.number,
};

export default memo(InfoTableTooltip);
